import { useState } from "react";
import { ArrowUpRight, Bot, ShoppingBag, Sparkles, Check, Play, Tv, Brain } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Kicker } from "@/components/Section";
import Marquee from "@/components/Marquee";
import VoiceIntro from "@/components/VoiceIntro";
import Reveal from "@/components/Reveal";
import ParallaxImage from "@/components/ParallaxImage";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import EbookDownload from "@/components/EbookDownload";
import { coaches } from "@/data/coaches";

const ASSET = "https://customer-assets.emergentagent.com/job_web-editor-45/artifacts";
const AUDIO_DE = `${ASSET}/6kzsjnww_Deutsch.mp3`;
const AUDIO_EN = `${ASSET}/tmwyluow_ElevenLabs_2026-06-25T00_39_35_Jonas%20-%20Confident%20and%20Trustworthy_pvc_sp110_s24_sb51_se54_b_m2.mp3`;
const IMG_HERO_BG = `${ASSET}/dx9exmir_ChatGPT%20Image%2024.%20Juni%202026%2C%2020_29_15.png`;
const IMG_DEVICES = `${ASSET}/1jk63vh4_Golfcoaching.online%20auf%20modernen%20Gera%CC%88ten.png`;
const IMG_LAUNCH = `${ASSET}/xr3rlng8_Offizieller%20Start%20von%20Golfcoaching.online.png`;
const VIDEO_URL = `${ASSET}/765xptgs_Advert%20%28horizontal%2Bsubtitle%29.mp4`;
const VIDEO_POSTER = `${ASSET}/5e9yhvch_ChatGPT%20Image%2024.%20Juni%202026%2C%2021_03_15.png`;
const IMG_SHOWCASE = `${ASSET}/zbw0dm58_Digitale%20Golf-Coaching-Plattform%20auf%20Gera%CC%88ten.png`;
const AI_IMG = "https://static.prod-images.emergentagent.com/jobs/956eab43-0661-4aa7-ab6c-dfc90d0a3225/images/4954879fffc71b30571847ff48f20d2e8593dfc2bbfe1f5087678dd26bad4a19.png";

const featIcons = [Tv, Bot, ShoppingBag, Sparkles];

const Onlinecoaching = () => {
  const { t, lang } = useLang();
  const o = t.online;
  const de = lang === "de";
  const href = `https://${o.ctaUrl}`;
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [golfIqOpen, setGolfIqOpen] = useState(false);
  const [aiCoachOpen, setAiCoachOpen] = useState(false);

  const golfIqUrl = "https://golfiq-video-series.emergent.host/?utm_source=share";
  const golfIq = de
    ? {
        kicker: "// VIDEO-MASTERCLASS // 6 FOLGEN",
        title: "Golf IQ statt Golfschwung",
        intro:
          "Besser spielen. Klüger entscheiden. Niedriger Score. Die kostenlose Video-Serie von golfcoaching.online – du brauchst keinen besseren Schwung, um besser zu scoren.",
        stats: [["10K+", "Analysierte Schwünge"], ["6", "Folgen"], ["FREE", "Masterclass"]],
        epTitle: "Was du lernst – 6 Folgen",
        episodes: [
          "Spiele gegen Fehler, nicht gegen den Platz",
          "Die Fahne ist keine Falle – Ziel: Mitte Grün",
          "Hero-Shots sind Ego-Schläge",
          "Winkel schlägt Entfernung",
          "Emotionen kosten Schläge",
          "Spiele den Schlag, den du selten bereust",
        ],
        benefits: [
          "100% kostenlos – keine versteckten Kosten",
          "Sofortiger Zugang nach der Anmeldung",
          "6 Folgen von PGA-zertifizierten Coaches",
          "Senke deinen Score ohne Technik-Umbau",
        ],
        cta: "Jetzt kostenlos starten",
        btn: "Gratis Serie: Golf IQ statt Golfschwung",
        btnSub: "Kostenlose Video-Masterclass · 6 Folgen",
      }
    : {
        kicker: "// VIDEO MASTERCLASS // 6 EPISODES",
        title: "Golf IQ over golf swing",
        intro:
          "Play better. Decide smarter. Lower your score. The free video series by golfcoaching.online – you don't need a better swing to score better.",
        stats: [["10K+", "Swings analysed"], ["6", "Episodes"], ["FREE", "Masterclass"]],
        epTitle: "What you'll learn – 6 episodes",
        episodes: [
          "Play against mistakes, not the course",
          "The pin is not a trap – aim for the centre",
          "Hero shots are ego shots",
          "Angle beats distance",
          "Emotions cost strokes",
          "Play the shot you'll rarely regret",
        ],
        benefits: [
          "100% free – no hidden costs",
          "Instant access after sign-up",
          "6 episodes from PGA-certified coaches",
          "Lower your score without rebuilding your technique",
        ],
        cta: "Start for free now",
        btn: "Free series: Golf IQ over golf swing",
        btnSub: "Free video masterclass · 6 episodes",
      };

  const aiCoachUrl = "https://putt-perfect-ai-1.emergent.host/?utm_source=share";
  const aiCoach = de
    ? {
        kicker: "// AI SWING LAB // v2.6",
        title: "Schwunganalyse per KI",
        intro:
          "Lade ein 30-Sekunden-Schwungvideo hoch. Unsere KI – trainiert mit dem geballten Wissen unseres Coaching-Teams – scannt deine Mechanik, deckt versteckte Fehler auf und erstellt deinen persönlichen Drill-Plan als PDF zum Download.",
        stats: [["10K+", "Analysierte Schwünge"], ["PGA", "Coaching-Standard"], ["<60s", "Report fertig"]],
        epTitle: "In 4 Schritten",
        episodes: [
          "Schwungvideo hochladen (5–10 Sek. reichen)",
          "Beschreiben, wie sich der Schwung anfühlt",
          "Deine Daten eingeben",
          "Analyse & persönlicher Drill-Plan als PDF",
        ],
        benefits: [
          "24/7 KI Golf Coach – immer für dich da",
          "Alle Masterclass-Videos von PGA-Pros & Ex-Tour-Spielern",
          "Persönliche Drill-Sheets statt Standard-Tipps",
          "Dein Video wird direkt nach der Analyse gelöscht",
        ],
        cta: "Meinen Schwung analysieren",
        btn: "KI Schwunganalyse: Lade dein Video hoch",
        btnSub: "AI Swing Lab · Persönlicher Drill-Plan in unter 60 Sek.",
      }
    : {
        kicker: "// AI SWING LAB // v2.6",
        title: "AI swing analysis",
        intro:
          "Upload a 30-second swing video. Our AI – trained with the combined knowledge of our coaching team – scans your mechanics, exposes hidden flaws and prescribes a personal drill sheet as a PDF download.",
        stats: [["10K+", "Swings analysed"], ["PGA", "Coaching standard"], ["<60s", "Report delivery"]],
        epTitle: "In 4 steps",
        episodes: [
          "Upload your swing video (5–10 sec is enough)",
          "Describe how the swing feels",
          "Enter your data",
          "Analysis & personal drill sheet as PDF",
        ],
        benefits: [
          "24/7 AI golf coach – always ready",
          "All masterclass videos from PGA pros & ex-tour players",
          "Personal drill sheets instead of generic tips",
          "Your video is deleted right after the analysis",
        ],
        cta: "Analyze my swing",
        btn: "AI swing analysis: upload your video",
        btnSub: "AI Swing Lab · personal drill plan in under 60 sec.",
      };

  return (
    <div data-testid="onlinecoaching-page" className="overflow-hidden">
      {/* HERO — cinematic studio background */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <ParallaxImage src={IMG_HERO_BG} alt="" direction="up" intensity={90} className="w-full h-full" imgClassName="object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-8 items-center">
          <div className="max-w-2xl fade-up">
            <div className="mt-5 inline-flex items-center gap-2 border border-[#ff0033]/50 bg-[#ff0033]/10 px-3 py-1.5">
              <span className="w-2 h-2 bg-[#ff0033] rounded-full animate-pulse" />
              <span className="font-mono-lab text-[10px] md:text-xs tracking-[0.2em] text-white uppercase">{o.badge}</span>
            </div>
            <h1 className="font-heading text-6xl sm:text-7xl md:text-8xl xl:text-9xl uppercase tracking-tight leading-[0.85] text-white mt-6">
              {o.h1a}
              <br />
              <span className="text-[#ff0033]">{o.h1b}</span>
            </h1>
            <p className="text-neutral-200 text-base md:text-lg max-w-xl mt-6 leading-relaxed">{o.intro}</p>
            <p className="font-heading text-xl md:text-3xl uppercase tracking-tight text-[#ff0033] mt-5" style={{ textShadow: "0 0 18px rgba(255,0,51,0.5)" }}>
              {o.netflix}
            </p>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mt-9">
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid="online-platform-cta"
                className="inline-flex items-center justify-center gap-3 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-8 py-5 text-lg hover:bg-[#cc0029] transition-colors"
              >
                <Play size={20} fill="currentColor" /> {o.watch}
              </a>
            </div>

            <div className="mt-8">
              <VoiceIntro src={lang === "de" ? AUDIO_DE : AUDIO_EN} lang={lang} stop={videoPlaying} />
            </div>
          </div>

          {/* Geräte-Mockup rechts vom Text, mit rotem Leuchten dahinter */}
          <div className="hidden lg:flex relative items-center justify-center fade-up" data-testid="online-devices">
            <div
              className="pointer-events-none absolute inset-0 -z-0"
              style={{ background: "radial-gradient(closest-side, rgba(255,0,51,0.45), rgba(255,0,51,0.14) 55%, transparent 74%)", filter: "blur(26px)" }}
            />
            <img
              src="/media/online-devices.png"
              alt={de ? "Golfcoaching.online auf Laptop und Smartphone" : "Golfcoaching.online on laptop and smartphone"}
              className="relative z-10 w-full max-w-xl object-contain drop-shadow-[0_25px_60px_rgba(255,0,51,0.35)]"
              data-testid="online-devices-img"
            />
          </div>
          </div>
        </div>
      </section>

      {/* FUNNEL Banner Buttons — Golf IQ + KI Coach + E-Books */}
      <section className="pt-16 md:pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="funnel-banners">
        <Reveal direction="up" className="mb-8">
          <Kicker>{de ? "// Kostenlose Online Hilfe" : "// Free online help"}</Kicker>
          <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight text-white mt-3 leading-none">
            {de ? "Kostenlose Online Hilfe – schnell verbessern, ohne Kosten" : "Free online help – improve fast, at no cost"}
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          <Reveal direction="up" className="h-full">
            <button
              onClick={() => setGolfIqOpen(true)}
              data-testid="golfiq-open-btn"
              className="group h-full w-full text-left bg-[#0F0F0F] hover:bg-[#141414] transition-colors p-7 md:p-8 flex flex-col"
            >
              <Brain size={34} className="text-[#ff0033]" />
              <span className="block font-mono-lab text-[10px] tracking-[0.2em] text-[#ff0033] uppercase mt-6">
                {de ? "// Kostenlose Video-Serie" : "// Free video series"}
              </span>
              <span className="block font-heading text-2xl md:text-3xl uppercase tracking-tight text-white mt-1.5 leading-none group-hover:text-[#ff0033] transition-colors">
                {golfIq.btn}
              </span>
              <span className="block text-neutral-400 text-sm mt-4 leading-relaxed flex-1">{golfIq.btnSub}</span>
              <span className="inline-flex items-center gap-1.5 text-[#ff0033] font-heading text-xs uppercase tracking-wider mt-6 group-hover:gap-2.5 transition-all">
                {de ? "Mehr erfahren" : "Learn more"} <ArrowUpRight size={15} />
              </span>
            </button>
          </Reveal>

          <Reveal direction="up" delay={0.08} className="h-full">
            <button
              onClick={() => setAiCoachOpen(true)}
              data-testid="aicoach-open-btn"
              className="group h-full w-full text-left bg-[#0F0F0F] hover:bg-[#141414] transition-colors p-7 md:p-8 flex flex-col"
            >
              <Bot size={34} className="text-[#ff0033]" />
              <span className="block font-mono-lab text-[10px] tracking-[0.2em] text-[#ff0033] uppercase mt-6">
                {de ? "// KI Swing-Analyse" : "// AI swing analysis"}
              </span>
              <span className="block font-heading text-2xl md:text-3xl uppercase tracking-tight text-white mt-1.5 leading-none group-hover:text-[#ff0033] transition-colors">
                {aiCoach.btn}
              </span>
              <span className="block text-neutral-400 text-sm mt-4 leading-relaxed flex-1">{aiCoach.btnSub}</span>
              <span className="inline-flex items-center gap-1.5 text-[#ff0033] font-heading text-xs uppercase tracking-wider mt-6 group-hover:gap-2.5 transition-all">
                {de ? "Mehr erfahren" : "Learn more"} <ArrowUpRight size={15} />
              </span>
            </button>
          </Reveal>

          <Reveal direction="up" delay={0.16} className="h-full">
            <EbookDownload variant="drive" lang={lang} />
          </Reveal>

          <Reveal direction="up" delay={0.24} className="h-full">
            <EbookDownload variant="spin" lang={lang} />
          </Reveal>
        </div>
      </section>

      {/* MASTERCLASSES — holistic topics */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="masterclasses-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal direction="left" className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative">
              <div className="absolute inset-0 bg-[#ff0033]/20 blur-[80px] rounded-full" />
              <img
                src={IMG_DEVICES}
                alt="golfcoaching.online masterclasses"
                className="relative w-full h-auto"
                style={{
                  WebkitMaskImage:
                    "radial-gradient(ellipse 78% 78% at 50% 48%, #000 60%, rgba(0,0,0,0.6) 78%, transparent 100%)",
                  maskImage:
                    "radial-gradient(ellipse 78% 78% at 50% 48%, #000 60%, rgba(0,0,0,0.6) 78%, transparent 100%)",
                }}
              />
            </div>
          </Reveal>

          <Reveal direction="right" delay={0.1} className="lg:col-span-6 order-1 lg:order-2">
            <Kicker>{o.mcKicker}</Kicker>
            <h2 className="font-heading text-4xl md:text-6xl uppercase tracking-tight text-white mt-3 leading-none">{o.mcTitle}</h2>
            <p className="text-neutral-400 text-base md:text-lg mt-6 leading-relaxed">{o.mcIntro}</p>

            <div className="flex flex-wrap gap-3 mt-8">
              {o.topics.map((topic, i) => (
                <span
                  key={topic}
                  data-testid={`masterclass-topic-${i}`}
                  className="font-mono-lab text-xs md:text-sm tracking-[0.15em] uppercase text-white border border-white/15 bg-[#0F0F0F] px-4 py-2.5 hover:border-[#ff0033] hover:text-[#ff0033] transition-colors"
                >
                  {topic}
                </span>
              ))}
            </div>

            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              data-testid="masterclass-cta"
              className="inline-flex items-center gap-3 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-8 py-4 text-lg hover:bg-[#cc0029] transition-colors mt-9"
            >
              {o.ctaText} <ArrowUpRight size={20} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* COACHES grid — Netflix cast style */}
      <section className="py-14 md:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="coaches-section">
        <Reveal direction="up">
          <Kicker>{o.teamKicker}</Kicker>
          <h2 className="font-heading text-4xl md:text-6xl uppercase tracking-tight text-white mt-3">{o.teamTitle}</h2>
          <p className="text-neutral-400 text-base md:text-lg max-w-2xl mt-4 leading-relaxed mb-10">{o.teamIntro}</p>
        </Reveal>

        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2.5 md:gap-3">
          {coaches.map((c, i) => (
            <Reveal
              key={c.name}
              direction="up"
              delay={(i % 6) * 0.05}
              data-testid={`coach-tile-${i}`}
              className="group relative overflow-hidden border border-white/10 bg-gradient-to-b from-[#484c52] to-[#31343a] aspect-[3/4]"
            >
              <img
                src={c.img}
                alt={c.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover object-top opacity-90 group-hover:scale-105 group-hover:opacity-100 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-2.5 md:p-3">
                <span className="font-mono-lab text-[8px] tracking-[0.12em] text-[#ff0033] uppercase">
                  {lang === "de" ? c.role_de : c.role_en}
                </span>
                <h3 className="font-heading text-sm md:text-base uppercase tracking-tight text-white leading-none mt-1">{c.name}</h3>
                <p className="font-mono-lab text-[8px] text-neutral-400 uppercase mt-1 hidden sm:block">
                  {lang === "de" ? c.tag_de : c.tag_en}
                </p>
              </div>
              <div className="absolute top-0 left-0 h-0.5 w-0 bg-[#ff0033] group-hover:w-full transition-all duration-500" />
            </Reveal>
          ))}
        </div>
      </section>

      {/* VIDEO banner */}
      <section className="relative" data-testid="video-banner">
        <div className="relative aspect-video max-h-[640px] w-full overflow-hidden border-y border-white/10 bg-black">
          {videoPlaying ? (
            <video
              src={VIDEO_URL}
              controls
              autoPlay
              playsInline
              data-testid="advert-video"
              className="absolute inset-0 w-full h-full object-contain bg-black"
            />
          ) : (
            <button
              onClick={() => setVideoPlaying(true)}
              data-testid="video-play-btn"
              className="absolute inset-0 flex items-center justify-center group cursor-pointer"
              aria-label={o.watch}
            >
              <img src={VIDEO_POSTER} alt="golfcoaching.online Trailer" className="absolute inset-0 w-full h-full object-cover" />
              <span className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors" />
              <span className="relative w-24 h-24 md:w-28 md:h-28 bg-[#ff0033] rounded-full flex items-center justify-center shadow-[0_0_60px_rgba(255,0,51,0.6)] group-hover:scale-110 transition-transform">
                <Play size={40} className="text-white ml-1" fill="currentColor" />
              </span>
            </button>
          )}
        </div>
      </section>

      {/* PLATFORM features */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="online-features">
        <Reveal direction="up">
          <Kicker>{o.featKicker}</Kicker>
          <h2 className="font-heading text-4xl md:text-6xl uppercase tracking-tight text-white mt-3 mb-10">{o.featTitle}</h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {o.features.map((f, i) => {
            const Icon = featIcons[i % featIcons.length];
            return (
              <Reveal key={f.t} direction="up" delay={(i % 4) * 0.06} data-testid={`online-feature-${i}`} className="bg-[#0F0F0F] p-8 hover:bg-[#141414] transition-colors group">
                <Icon size={28} className="text-[#ff0033]" />
                <h3 className="font-heading text-2xl uppercase tracking-tight text-white mt-5 group-hover:text-[#ff0033] transition-colors">{f.t}</h3>
                <p className="text-neutral-400 text-sm mt-3 leading-relaxed">{f.d}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* MEMBERSHIP PLANS */}
      <section className="py-20 md:py-28 bg-[#0A0A0A] border-y border-white/10" data-testid="plans-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal direction="up" className="text-center max-w-3xl mx-auto">
            <Kicker>{o.plansKicker}</Kicker>
            <h2 className="font-heading text-4xl md:text-6xl uppercase tracking-tight text-white mt-3">{o.plansTitle}</h2>
            <p className="font-mono-lab text-xs md:text-sm tracking-[0.2em] text-[#ff0033] uppercase mt-6 mb-4">{o.plansIncludeTitle}</p>
            <ul className="flex flex-col items-center gap-2.5">
              {o.includes.map((inc) => (
                <li key={inc} className="flex items-center gap-2 text-neutral-300 text-sm md:text-base">
                  <Check size={15} className="text-[#ff0033] shrink-0" /> {inc}
                </li>
              ))}
            </ul>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mt-14">
            {o.plans.map((plan, i) => (
              <Reveal
                key={plan.name}
                direction="up"
                delay={(i % 4) * 0.06}
                data-testid={`plan-card-${i}`}
                className={`relative flex flex-col bg-[#0F0F0F] border p-8 transition-colors ${
                  plan.popular ? "border-[#ff0033]" : "border-white/10 hover:border-[#ff0033]/50"
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ff0033] text-white font-mono-lab text-[10px] tracking-[0.2em] uppercase px-3 py-1">
                    {o.popular}
                  </span>
                )}
                <h3 className="font-heading text-2xl uppercase tracking-tight text-white text-center leading-tight min-h-[60px] flex items-center justify-center">
                  {plan.name}
                </h3>
                <div className="flex items-start justify-center gap-1 mt-4">
                  <span className="font-heading text-2xl text-white mt-2">€</span>
                  <span className="font-heading text-6xl text-white leading-none">{plan.price}</span>
                </div>
                <p className="font-mono-lab text-[10px] tracking-[0.2em] text-neutral-500 uppercase text-center mt-3">{plan.per}</p>
                <p className="text-neutral-400 text-xs text-center mt-3 min-h-[40px]">{plan.valid}</p>

                <a
                  href="https://www.golfcoaching.online/plans"
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`plan-cta-${i}`}
                  className="block text-center bg-[#ff0033] text-white font-heading uppercase tracking-wider py-3.5 text-base hover:bg-[#cc0029] transition-colors mt-6"
                >
                  {o.trial}
                </a>

                <ul className="space-y-3 mt-8 pt-8 border-t border-white/10">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-neutral-300 text-sm">
                      <Check size={15} className="text-[#ff0033] shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SHOWCASE + final CTA */}
      <section className="py-16 md:py-24 bg-[#0A0A0A] border-t border-white/10" data-testid="showcase-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal direction="left" className="lg:col-span-6 order-2 lg:order-1">
            <img src={IMG_SHOWCASE} alt="golfcoaching.online on devices" className="w-full h-auto" />
          </Reveal>
          <Reveal direction="right" delay={0.1} className="lg:col-span-6 order-1 lg:order-2">
            <h2 className="font-heading text-4xl md:text-6xl uppercase tracking-tight text-white leading-none">{o.showcaseTitle}</h2>
            <p className="font-heading text-xl md:text-2xl uppercase tracking-tight text-white mt-6 border-l-2 border-[#ff0033] pl-5">{o.oneStop}</p>
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              data-testid="online-platform-cta-bottom"
              className="inline-flex items-center gap-3 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-8 py-5 text-lg md:text-xl hover:bg-[#cc0029] transition-colors mt-9"
            >
              {o.ctaText} · {o.ctaUrl}
              <ArrowUpRight size={22} />
            </a>
          </Reveal>
        </div>
      </section>

      <Marquee />

      {/* GOLF IQ popup */}
      <Dialog open={golfIqOpen} onOpenChange={setGolfIqOpen}>
        <DialogContent className="max-w-2xl bg-[#0B0B0B] border-2 border-[#ff0033] text-white max-h-[90vh] overflow-y-auto" data-testid="golfiq-modal" style={{ boxShadow: "0 0 40px rgba(255,0,51,0.35)" }}>
          <DialogTitle className="sr-only">{golfIq.title}</DialogTitle>
          <span className="font-mono-lab text-[10px] md:text-xs tracking-[0.2em] text-[#ff0033] uppercase">{golfIq.kicker}</span>
          <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight text-white mt-2 leading-none">
            {golfIq.title}
          </h2>
          <p className="text-neutral-300 text-base md:text-lg mt-4 leading-relaxed">{golfIq.intro}</p>

          <div className="grid grid-cols-3 gap-3 mt-6">
            {golfIq.stats.map(([num, label]) => (
              <div key={label} className="bg-[#0F0F0F] border border-white/10 p-4 text-center">
                <div className="font-heading text-2xl md:text-3xl text-[#ff0033] leading-none">{num}</div>
                <div className="font-mono-lab text-[9px] md:text-[10px] tracking-[0.15em] text-neutral-400 uppercase mt-2">{label}</div>
              </div>
            ))}
          </div>

          <p className="font-mono-lab text-xs tracking-[0.2em] text-[#ff0033] uppercase mt-7 mb-3">// {golfIq.epTitle}</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
            {golfIq.episodes.map((ep, i) => (
              <li key={ep} className="flex items-start gap-3 text-neutral-200">
                <span className="font-heading text-[#ff0033] text-sm leading-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm md:text-base">{ep}</span>
              </li>
            ))}
          </ul>

          <ul className="mt-7 space-y-2.5 border-t border-white/10 pt-6">
            {golfIq.benefits.map((b) => (
              <li key={b} className="flex items-center gap-3 text-neutral-200">
                <Check size={16} className="text-[#ff0033] shrink-0" />
                <span className="text-sm md:text-base">{b}</span>
              </li>
            ))}
          </ul>

          <a
            href={golfIqUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="golfiq-cta-link"
            className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-8 py-4 text-lg hover:bg-[#cc0029] transition-colors"
          >
            {golfIq.cta} <ArrowUpRight size={20} />
          </a>
        </DialogContent>
      </Dialog>

      {/* KI COACH popup */}
      <Dialog open={aiCoachOpen} onOpenChange={setAiCoachOpen}>
        <DialogContent className="max-w-2xl bg-[#0B0B0B] border-2 border-[#ff0033] text-white max-h-[90vh] overflow-y-auto" data-testid="aicoach-modal" style={{ boxShadow: "0 0 40px rgba(255,0,51,0.35)" }}>
          <DialogTitle className="sr-only">{aiCoach.title}</DialogTitle>
          <span className="font-mono-lab text-[10px] md:text-xs tracking-[0.2em] text-[#ff0033] uppercase">{aiCoach.kicker}</span>
          <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight text-white mt-2 leading-none">
            {aiCoach.title}
          </h2>
          <p className="text-neutral-300 text-base md:text-lg mt-4 leading-relaxed">{aiCoach.intro}</p>

          <div className="mt-6 relative border border-white/10 overflow-hidden bg-black">
            <img
              src={AI_IMG}
              alt="KI Schwunganalyse am Smartphone mit persönlichem Trainingsplan"
              className="w-full h-auto max-h-[300px] object-cover object-center block"
              data-testid="aicoach-modal-img"
            />
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#ff0033] to-transparent" />
          </div>

          <div className="grid grid-cols-3 gap-3 mt-6">
            {aiCoach.stats.map(([num, label]) => (
              <div key={label} className="bg-[#0F0F0F] border border-white/10 p-4 text-center">
                <div className="font-heading text-2xl md:text-3xl text-[#ff0033] leading-none">{num}</div>
                <div className="font-mono-lab text-[9px] md:text-[10px] tracking-[0.15em] text-neutral-400 uppercase mt-2">{label}</div>
              </div>
            ))}
          </div>

          <p className="font-mono-lab text-xs tracking-[0.2em] text-[#ff0033] uppercase mt-7 mb-3">// {aiCoach.epTitle}</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
            {aiCoach.episodes.map((ep, i) => (
              <li key={ep} className="flex items-start gap-3 text-neutral-200">
                <span className="font-heading text-[#ff0033] text-sm leading-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm md:text-base">{ep}</span>
              </li>
            ))}
          </ul>

          <ul className="mt-7 space-y-2.5 border-t border-white/10 pt-6">
            {aiCoach.benefits.map((b) => (
              <li key={b} className="flex items-center gap-3 text-neutral-200">
                <Check size={16} className="text-[#ff0033] shrink-0" />
                <span className="text-sm md:text-base">{b}</span>
              </li>
            ))}
          </ul>

          <a
            href={aiCoachUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="aicoach-cta-link"
            className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-8 py-4 text-lg hover:bg-[#cc0029] transition-colors"
          >
            {aiCoach.cta} <ArrowUpRight size={20} />
          </a>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Onlinecoaching;
